import { useMemo } from 'react';
import { FileText, Loader2, CheckCircle2, XCircle, Clock, Layers } from 'lucide-react';
import { useJobProgressStream, type ProgressEvent } from './useJobProgressStream';

/**
 * Chunks panel — visualizes the chunked DP extraction progress.
 *
 * The worker emits these chunk-related events:
 *   dp.chunk.plan    — once per document, lists the planned chunks
 *   dp.chunk.start   — per chunk, when its Claude call begins
 *   dp.chunk.done    — per chunk, when its result was parsed successfully
 *   dp.chunk.failed  — per chunk, when extraction or schema validation failed
 *   dp.chunk.merge   — once per document, after all chunks merged
 *
 * We replay the events and reduce them into the current state of every
 * chunk of every document. State machine per chunk:
 *
 *   queued (default) → running → done | failed
 *
 * The panel renders one tile per chunk, grouped by document. Tiles show
 * page range + current state. Useful for users to see which specific
 * page range is in flight at any moment and which (if any) failed.
 */

type ChunkState = 'queued' | 'running' | 'done' | 'failed';

interface ChunkInfo {
  index: number;
  total: number;
  label: string;        // e.g. "pages 6-10"
  page_from?: number;
  page_to?: number;
  state: ChunkState;
  holdings?: number;
  error?: string;
}

interface DocInfo {
  filename: string;
  chunks: ChunkInfo[];
  merged?: { succeeded: number; failed: number; merged_holdings: number };
}

const STATE_STYLES: Record<ChunkState, { icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>; label: string; ring: string; text: string; bg: string }> = {
  queued: {
    icon: Clock,
    label: 'Queued',
    ring: 'var(--glass-border)',
    text: 'var(--text-400)',
    bg: 'var(--bg-hover)',
  },
  running: {
    icon: Loader2,
    label: 'Extracting',
    ring: 'var(--accent)',
    text: 'var(--accent)',
    bg: 'var(--accent-soft)',
  },
  done: {
    icon: CheckCircle2,
    label: 'Done',
    ring: 'rgba(61, 214, 140, 0.4)',
    text: 'var(--status-ok)',
    bg: 'color-mix(in srgb, var(--status-ok) 10%, transparent)',
  },
  failed: {
    icon: XCircle,
    label: 'Failed',
    ring: 'rgba(255, 80, 80, 0.4)',
    text: 'var(--status-err)',
    bg: 'color-mix(in srgb, var(--status-err) 10%, transparent)',
  },
};

export function ChunksPanel({ jobId, apiBaseUrl }: { jobId: string; apiBaseUrl: string }) {
  const { events } = useJobProgressStream(jobId, apiBaseUrl);
  const docs = useMemo(() => reduceChunkEvents(events), [events]);

  // Nothing to show yet
  if (docs.length === 0) return null;

  return (
    <div className="card mb-6">
      <div className="px-5 py-3 flex items-center gap-2" style={{ borderBottom: '1px solid var(--glass-border)' }}>
        <Layers className="w-4 h-4" style={{ color: 'var(--accent)' }} />
        <h3 className="text-sm font-semibold text-ink-strong">Document chunks</h3>
        <span className="text-xs text-ink-400">
          · {docs.reduce((sum, d) => sum + d.chunks.length, 0)} chunk{docs.reduce((sum, d) => sum + d.chunks.length, 0) === 1 ? '' : 's'} across {docs.length} document{docs.length === 1 ? '' : 's'}
        </span>
      </div>
      <div className="p-5 space-y-5">
        {docs.map(doc => (
          <DocBlock key={doc.filename} doc={doc} />
        ))}
      </div>
    </div>
  );
}

function DocBlock({ doc }: { doc: DocInfo }) {
  const done = doc.chunks.filter(c => c.state === 'done').length;
  const failed = doc.chunks.filter(c => c.state === 'failed').length;
  const running = doc.chunks.filter(c => c.state === 'running').length;
  const queued = doc.chunks.filter(c => c.state === 'queued').length;

  return (
    <div>
      {/* Document header */}
      <div className="flex items-baseline justify-between mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <FileText className="w-4 h-4 text-ink-400 shrink-0" />
          <div className="font-medium text-ink-strong tracking-tight truncate font-mono text-sm">{doc.filename}</div>
        </div>
        <div className="text-xs text-ink-400 font-mono tabular-nums shrink-0 ml-3">
          {done}/{doc.chunks.length} done
          {running > 0 && <span className="ml-2" style={{ color: 'var(--accent)' }}>· {running} running</span>}
          {queued > 0 && <span className="ml-2">· {queued} queued</span>}
          {failed > 0 && <span className="ml-2" style={{ color: 'var(--status-err)' }}>· {failed} failed</span>}
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-1.5 rounded-full overflow-hidden mb-3" style={{ background: 'var(--bg-hover)' }}>
        <div
          className="h-full transition-all duration-300"
          style={{
            width: `${(done / Math.max(1, doc.chunks.length)) * 100}%`,
            background: failed > 0
              ? 'linear-gradient(90deg, var(--accent) 0%, var(--status-err) 100%)'
              : 'linear-gradient(90deg, var(--accent) 0%, var(--accent-glow) 100%)',
          }}
        />
      </div>

      {/* Chunk tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
        {doc.chunks.map(c => <ChunkTile key={c.index} chunk={c} />)}
      </div>

      {/* Merge result */}
      {doc.merged && (
        <div className="mt-3 text-xs text-ink-400 tracking-tight">
          Merged into <span className="text-ink-strong font-semibold">{doc.merged.merged_holdings}</span> unique holding{doc.merged.merged_holdings === 1 ? '' : 's'}
          {doc.merged.failed > 0 && (
            <span style={{ color: 'var(--status-warn)' }}> · {doc.merged.failed} chunk(s) failed and were skipped</span>
          )}
        </div>
      )}
    </div>
  );
}

function ChunkTile({ chunk }: { chunk: ChunkInfo }) {
  const style = STATE_STYLES[chunk.state];
  const Icon = style.icon;
  return (
    <div
      className="rounded-xl p-2.5 flex items-center gap-2.5"
      style={{
        background: style.bg,
        border: `1px solid ${style.ring}`,
      }}
      title={chunk.error ? `${chunk.label} — ${chunk.error}` : `${chunk.label} (${style.label})`}
    >
      <Icon
        className={`w-4 h-4 shrink-0 ${chunk.state === 'running' ? 'animate-spin' : ''}`}
        style={{ color: style.text }}
      />
      <div className="min-w-0 flex-1">
        <div className="text-xs font-semibold text-ink-strong tracking-tight font-mono truncate">
          {chunk.label}
        </div>
        <div className="text-[10px] tracking-wide truncate" style={{ color: style.text }}>
          {style.label}
          {chunk.state === 'done' && chunk.holdings !== undefined && ` · ${chunk.holdings} holding${chunk.holdings === 1 ? '' : 's'}`}
        </div>
      </div>
    </div>
  );
}

/** Reduce the SSE event stream into a per-document chunk state map. */
function reduceChunkEvents(events: ProgressEvent[]): DocInfo[] {
  const byFile = new Map<string, DocInfo>();

  for (const evt of events) {
    const detail = evt.detail ?? {};
    const filename = (detail.filename as string) ?? '';

    if (evt.phase === 'dp.chunk.plan' && filename) {
      const planned = Array.isArray(detail.chunks) ? (detail.chunks as Array<{
        index: number; label: string; page_from?: number; page_to?: number;
      }>) : [];
      byFile.set(filename, {
        filename,
        chunks: planned.map(p => ({
          index: p.index,
          total: planned.length,
          label: p.label,
          page_from: p.page_from,
          page_to: p.page_to,
          state: 'queued',
        })),
      });
      continue;
    }

    const doc = filename ? byFile.get(filename) : undefined;
    if (!doc) continue;

    if (evt.phase === 'dp.chunk.start') {
      const idx = detail.chunk_index as number | undefined;
      if (idx !== undefined) {
        const c = doc.chunks.find(c => c.index === idx);
        if (c) c.state = 'running';
      }
    } else if (evt.phase === 'dp.chunk.done') {
      const idx = detail.chunk_index as number | undefined;
      if (idx !== undefined) {
        const c = doc.chunks.find(c => c.index === idx);
        if (c) {
          c.state = 'done';
          c.holdings = (detail.holdings as number) ?? undefined;
        }
      }
    } else if (evt.phase === 'dp.chunk.failed') {
      const idx = detail.chunk_index as number | undefined;
      if (idx !== undefined) {
        const c = doc.chunks.find(c => c.index === idx);
        if (c) {
          c.state = 'failed';
          c.error = (detail.error as string) ?? evt.message;
        }
      }
    } else if (evt.phase === 'dp.chunk.merge') {
      doc.merged = {
        succeeded: (detail.succeeded as number) ?? 0,
        failed: (detail.failed as number) ?? 0,
        merged_holdings: (detail.merged_holdings as number) ?? 0,
      };
    }
  }

  return Array.from(byFile.values());
}
